import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, CircularProgress, FormControl, Grid, InputLabel, TextField, Typography } from '@material-ui/core';
import _ from 'lodash';
import { mobilizeiAppPublicationsService } from '../_services/mobilizei/appPublications.service';
import { apiErrorMessage } from '../_helpers/apiError';

const useStyles = makeStyles(() => ({
    hint: { fontSize: 12, color: '#777', marginTop: 4 },
    resultLabel: { fontWeight: 'bold', marginRight: 6 },
    resultRow: { marginBottom: 6 },
    success: { color: '#43a047', fontWeight: 'bold', marginBottom: 12 },
}));

const normalizeSlug = (raw) =>
    String(raw || '').normalize('NFD').toLowerCase().replace(/[^a-z0-9]/g, '');

export default function PrepareStoreStep({ store, setLocalStore, platformId, isFinished }) {
    const classes = useStyles();
    const [slug, setSlug] = useState(_.get(store, 'slug', ''));
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const normalized = normalizeSlug(slug);

    const handlePrepare = () => {
        setLoading(true);
        setError(null);
        mobilizeiAppPublicationsService.prepareStore({
            storeId: store.id,
            platformId: platformId || store.platformId,
            slug,
        })
            .then((res) => {
                setResult(res);
                // Update local store copy so subsequent steps see the new values
                setLocalStore(prev => ({
                    ...prev,
                    slug: res.slug,
                    expoAccountName: res.expoAccountName,
                    expoProjectId: res.expoProjectId,
                    firebaseProjectName: res.firebaseProjectName,
                    platforms: {
                        ...prev.platforms,
                        android: {
                            ...(prev.platforms?.android || {}),
                            bundleId: res.androidBundleId,
                            googlePlayServiceAccountName: res.googlePlayServiceAccountName,
                        },
                        ios: {
                            ...(prev.platforms?.ios || {}),
                            packageName: res.iosPackageName,
                        },
                    },
                }));
                isFinished(true);
            })
            .catch((e) => setError(apiErrorMessage(e, 'Erro ao preparar a loja.')))
            .finally(() => setLoading(false));
    };

    const ResultRow = ({ label, value }) => (
        <div className={classes.resultRow}>
            <span className={classes.resultLabel}>{label}:</span>{value || '-'}
        </div>
    );

    return (
        <Grid container direction="column">
            <Box mt={1} mb={2}>
                <Typography variant="body2">
                    Antes de publicar, é necessário preparar a loja. Informe o <b>slug</b> do app —
                    a partir dele serão derivados os bundle IDs, alocados os recursos (Expo, Firebase,
                    service account) e criado o projeto Expo.
                </Typography>
            </Box>

            {!result ? (
                <>
                    <FormControl fullWidth>
                        <InputLabel shrink>Slug</InputLabel>
                        <TextField
                            variant="standard"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            placeholder="ex.: meuapp"
                        />
                    </FormControl>
                    <div className={classes.hint}>
                        Será normalizado para: <b>{normalized || '(vazio)'}</b> (minúsculas, letras e números)
                    </div>
                    {error && (
                        <Typography variant="body2" style={{ color: '#c62828', marginTop: 8 }}>
                            {error}
                        </Typography>
                    )}
                    <Box mt={2}>
                        {loading ? (
                            <CircularProgress size={28} />
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={!normalized}
                                onClick={handlePrepare}
                            >
                                Preparar loja
                            </Button>
                        )}
                    </Box>
                </>
            ) : (
                <>
                    <Typography className={classes.success}>
                        ✅ Loja preparada com sucesso.
                    </Typography>
                    <ResultRow label="Slug" value={result.slug} />
                    <ResultRow label="Bundle ID (Android)" value={result.androidBundleId} />
                    <ResultRow label="Package name (iOS)" value={result.iosPackageName} />
                    <ResultRow label="Conta Expo" value={result.expoAccountName} />
                    <ResultRow label="Projeto Expo" value={result.expoProjectId} />
                    <ResultRow label="Projeto Firebase" value={result.firebaseProjectName} />
                    <ResultRow label="Service account" value={result.googlePlayServiceAccountName} />
                    <Box mt={2}>
                        <Typography variant="body2" style={{ color: '#555' }}>
                            Clique em <strong>Próximo</strong> para continuar.
                        </Typography>
                    </Box>
                </>
            )}
        </Grid>
    );
}
